/**
 * `EQUIPE-MODALS`, frame 82: **the absence's return day, read as a person writes it.** « 28 octobre », « 1er novembre », « 28/10 » and
 * « 28/10/2026 »; the answer is the `YYYY-MM-DD` the door takes (`PATCH /users/{id}/availability`), or `null` when the text is not a day.
 *
 * It is read against THE DOOR'S day (`today`, the team read's `asOf`), never the browser's clock (A50; `no-ambient-time`). A day written
 * without its year is the next such day on or after `today`, never a past one; one written WITH its year is taken as written and refused
 * when it is already gone. Without `today`, only a day with its year can be placed.
 */
const MONTHS: ReadonlyMap<string, number> = new Map([
  ["janvier", 1],
  ["janv", 1],
  ["fevrier", 2],
  ["fevr", 2],
  ["mars", 3],
  ["avril", 4],
  ["avr", 4],
  ["mai", 5],
  ["juin", 6],
  ["juillet", 7],
  ["juil", 7],
  ["aout", 8],
  ["septembre", 9],
  ["sept", 9],
  ["octobre", 10],
  ["oct", 10],
  ["novembre", 11],
  ["nov", 11],
  ["decembre", 12],
  ["dec", 12],
]);

const DAY_FIRST = /^(\d{1,2})(?:er)?\s+([a-z]+)\.?(?:\s+(\d{4}))?$/u;
const NUMERIC = /^(\d{1,2})[/.-](\d{1,2})(?:[/.-](\d{4}))?$/u;

const isLeap = (year: number): boolean => (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0;
const daysIn = (year: number, month: number): number =>
  month === 2 ? (isLeap(year) ? 29 : 28) : [4, 6, 9, 11].includes(month) ? 30 : 31;
const iso = (year: number, month: number, day: number): string =>
  `${String(year).padStart(4, "0")}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;

export function readReturnDay(text: string, today: string | null): string | null {
  const plain = text.trim().toLowerCase().normalize("NFD").replace(/\p{M}/gu, "").replace(/\s+/gu, " ");
  if (plain === "") return null;

  let day: number;
  let month: number;
  let year: number | null;
  const worded = DAY_FIRST.exec(plain);
  const numeric = worded === null ? NUMERIC.exec(plain) : null;
  if (worded !== null) {
    day = Number(worded[1]);
    month = MONTHS.get(worded[2] ?? "") ?? 0;
    year = worded[3] === undefined ? null : Number(worded[3]);
  } else if (numeric !== null) {
    day = Number(numeric[1]);
    month = Number(numeric[2]);
    year = numeric[3] === undefined ? null : Number(numeric[3]);
  } else {
    return null;
  }
  if (month < 1 || month > 12 || day < 1) return null;

  const todayYear = today === null ? null : Number(today.slice(0, 4));
  if (year !== null) {
    if (day > daysIn(year, month)) return null;
    const written = iso(year, month, day);
    return today !== null && written < today ? null : written;
  }
  // No year: the next such day on or after the door's day. Four years is enough to reach a 29 février.
  if (today === null || todayYear === null || Number.isNaN(todayYear)) return null;
  for (let candidate = todayYear; candidate <= todayYear + 4; candidate += 1) {
    if (day > daysIn(candidate, month)) continue;
    const found = iso(candidate, month, day);
    if (found >= today) return found;
  }
  return null;
}
